/**
 * 🔒 A company's other names and the close-employer rule (piece 10, piece 13;
 * decision 7150; T3026). The names are read only where a person's employer is
 * matched by name, which is the record checks. 🔨 Nothing here searches a
 * company by an other name on a list, a picker or the Duplicates report.
 */
import { normaliseCompanyName } from './normalise';

/** The longest other name a company carries, in characters. */
export const COMPANY_OTHER_NAME_MAX = 200;

/** 🔒 The leading-words test's floor: the shorter folded name holds at least this many letters. */
export const CLOSE_LEADING_LETTERS = 5;

/** 🔒 The spelling test's bound: at most this many letters apart, and that count times five under the shorter name's length. */
export const CLOSE_SPELLING_EDITS = 2;
export const CLOSE_SPELLING_RATIO = 5;

/**
 * 🔒 The words that tell one branch from another by its compass point, whole
 * words and their abbreviations (decision 7150): two names that differ in one
 * are never close, so North is never read as South.
 */
export const DIRECTION_WORDS: readonly string[] = ['north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/** What tells sibling names apart: their digit runs and their direction words, each in order of appearance sorted. */
function siblingMarks(key: string): string {
  const digits = (key.match(/\d+/g) ?? []).sort().join(',');
  const directions = key.split(' ').filter((word) => DIRECTION_WORDS.includes(word)).sort().join(',');
  return `${digits}|${directions}`;
}

/** The route Edit Company saves the whole list through. */
export function companyOtherNamesPath(companyId: string): string {
  return `/company-other-names/company/${encodeURIComponent(companyId)}`;
}

/** The letters a folded name holds, its spaces aside. */
function letters(key: string): number {
  return key.replace(/ /g, '').length;
}

/** The edit distance between two strings, stopping once it passes `limit`. */
export function boundedEditDistance(left: string, right: string, limit = Number.POSITIVE_INFINITY): number {
  if (Math.abs(left.length - right.length) > limit) return limit + 1;
  let previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    let best = i;
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      const value = Math.min(previous[j]! + 1, current[j - 1]! + 1, previous[j - 1]! + cost);
      current.push(value);
      if (value < best) best = value;
    }
    if (best > limit) return limit + 1;
    previous = current;
  }
  return previous[right.length]!;
}

/**
 * 🔒 Whether two folded company names are close and not equal (decision 7150).
 * The leading-words test: every word of the shorter opens the longer, in order,
 * and the shorter holds at least five letters. The spelling test: one or two
 * letters apart, and that count times five under the shorter name's length.
 * Neither holds where the two differ in a digit run or a direction word. It
 * reads closeness folds, so a lone *s* is a word the source wrote alone.
 */
export function foldedNamesClose(left: string, right: string): boolean {
  if (left === '' || right === '' || left === right) return false;
  // 🔒 A numbered or compass-point sibling is not close, whichever side carries the number or the point.
  if (siblingMarks(left) !== siblingMarks(right)) return false;
  const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
  const short = shorter.split(' ');
  const long = longer.split(' ');
  if (short.length < long.length && letters(shorter) >= CLOSE_LEADING_LETTERS && short.every((word, index) => long[index] === word)) return true;
  const distance = boundedEditDistance(shorter, longer, CLOSE_SPELLING_EDITS);
  return distance >= 1 && distance <= CLOSE_SPELLING_EDITS && distance * CLOSE_SPELLING_RATIO < shorter.length;
}

/** The apostrophes a possessive or a contraction carries, straight and typographic. */
const APOSTROPHES = /['\u2018\u2019\u02BC]/g;

/**
 * 🔒 The fold the closeness rule reads (decision 7150): the company name routine
 * with each apostrophe folded to nothing first, so *Joe's* is the one word
 * *joes* and never a word *s* the direction test would read as south.
 */
export function closeNameKey(name: string): string {
  return normaliseCompanyName(name.replace(APOSTROPHES, ''));
}

/** 🔒 Whether two raw names are close by the rule on their closeness folds; two that differ only by an apostrophe are close. */
export function namesClose(left: string, right: string): boolean {
  const [one, two] = [closeNameKey(left), closeNameKey(right)];
  if (one === '' || two === '') return false;
  if (one === two) return normaliseCompanyName(left) !== normaliseCompanyName(right);
  return foldedNamesClose(one, two);
}

/** How an employer meets a company named by these names: the same company, a close one, or neither. */
export type EmployerMatch = 'same' | 'close' | null;

/** 🔒 An employer against a company's name and its other names (decision 7150): equal folded is the same company, else close by the rule. */
export function employerMatch(companyNames: readonly string[], employer: string): EmployerMatch {
  const key = normaliseCompanyName(employer);
  if (key === '') return null;
  const keys = companyNames.map((name) => normaliseCompanyName(name)).filter((one) => one !== '');
  if (keys.includes(key)) return 'same';
  return companyNames.some((name) => namesClose(name, employer)) ? 'close' : null;
}

/** The first three letters a close candidate's folded name opens with, which is how the file is narrowed before the rule runs. */
export function closeCandidatePrefix(employer: string): string {
  return normaliseCompanyName(employer).slice(0, 3);
}

/**
 * 🔒 What is wrong with a company's list of other names before any is written
 * (decision 7150): a blank or over-long name, one that folds to nothing, the
 * company's own name, or a name listed twice. Another company holding a name is
 * the server's to answer, because only it reads the file.
 */
export function otherNamesProblem(names: readonly string[], companyName: string): string | null {
  const own = normaliseCompanyName(companyName);
  const seen = new Set<string>();
  for (const raw of names) {
    const name = raw.trim();
    if (name === '') return 'An other name is blank; type it or remove the line.';
    if (name.length > COMPANY_OTHER_NAME_MAX) return `An other name is at most ${COMPANY_OTHER_NAME_MAX} characters.`;
    const key = normaliseCompanyName(name);
    if (key === '') return `“${name}” holds no letters a name is matched on.`;
    if (key === own) return `“${name}” is this company's own name, so it is not another one.`;
    if (seen.has(key)) return `“${name}” is listed twice.`;
    seen.add(key);
  }
  return null;
}

/** 🔒 The refusal where another company already goes by a name, naming that company. */
export function otherNameHeldSentence(name: string, companyName: string): string {
  return `“${name}” already names ${companyName}, so it cannot also name this company; merge the two companies if they are one.`;
}
